import * as React from 'react';
import hoistNonReactMethods from 'hoist-non-react-methods';
import { getDisplayName } from './utils/getDisplayName';
import { st, classes } from './Focusable.st.css';
import { isStatelessComponent } from './utils/isStatelessComponent';
import { shellClasses } from '../../../../../common/shellClasses';
import classNames from 'classnames';
/**
 * Assigned the given propTypes to the given class.
 *
 * This is a hack because since Yoshi3, with babel-preset-yoshi,
 * the babel-plugin-transform-react-remove-prop-types is enabled and removes propTypes.
 *
 * So if we simply do FocusableHOC.propTypes = Component.propTypes, it is being stripped away.
 *
 * This later becomes a problem if another component defines:
 * <code>
 * Comp.propTypes = {
 *   prop1: SomeFocusableComp.propTypes.prop1
 * }
 * </code>
 */
const assignPropTypesHack = (target, propTypes) => {
    target.propTypes = propTypes;
};
/**
 * Singleton for managing current input method (keyboard or mouse).
 */
const inputMethod = new (class {
    constructor() {
        // Default is keyboard in case an element is focused programmatically.
        this.method = 'keyboard';
        this.subscribers = new Map();
        this.subscribe = (target, callback) => this.subscribers.set(target, callback);
        this.unsubscribe = (target) => this.subscribers.delete(target);
        /**
         * Is the current input method `keyboard`. if `false` is means it is `mouse`
         */
        this.isKeyboard = () => this.method === 'keyboard';
        if (typeof window !== 'undefined') {
            window.addEventListener('mousedown', () => this.setMethod('mouse'));
            window.addEventListener('keydown', () => this.setMethod('keyboard'));
            // We need to listen on keyUp, in case a TAB is made from the browser's address-bar,
            // so the keyDown is not fired, only the keyUp.
            window.addEventListener('keyup', () => this.setMethod('keyboard'));
        }
    }
    setMethod(method) {
        if (method !== this.method) {
            this.method = method;
            this.subscribers.forEach((f) => f());
        }
    }
})();
export function withFocusable(Component, options = { hasCustomFocus: false }) {
    class Focusable extends React.Component {
        constructor() {
            super(...arguments);
            this.wrappedComponentRef = null;
            this.state = {
                focus: false,
                focusVisible: false,
            };
            this.focus = () => {
                if (this.wrappedComponentRef?.focus) {
                    this.wrappedComponentRef.focus();
                }
            };
            this.markAsFocused = () => {
                this.setState({
                    focus: true,
                    focusVisible: options.isFocusWithMouse || inputMethod.isKeyboard(),
                });
                inputMethod.subscribe(this, () => {
                    if (options.isFocusWithMouse || inputMethod.isKeyboard()) {
                        this.setState({ focusVisible: true });
                    }
                });
            };
            this.markAsBlurred = () => {
                inputMethod.unsubscribe(this);
                this.setState({ focus: false, focusVisible: false });
            };
            this.onFocus = (event) => {
                const { onFocus } = this.props;
                onFocus ? onFocus(event, this.getTriggers()) : this.markAsFocused();
            };
            this.onBlur = (event) => {
                const { onBlur } = this.props;
                onBlur ? onBlur(event, this.getTriggers()) : this.markAsBlurred();
            };
        }
        static { this.displayName = getDisplayName(Component); }
        static { this.defaultProps = Component.defaultProps; }
        componentWillUnmount() {
            inputMethod.unsubscribe(this);
        }
        componentDidUpdate(prevProps) {
            /*
              in case when button was focused and then become disabled,
              we need to trigger blur logic and remove all listers, as disabled button
              do not trigger onFocus and onBlur events
            */
            const isFocused = this.state.focus || this.state.focusVisible;
            const isBecomeDisabled = !prevProps.disabled && this.props.disabled;
            if (isFocused && isBecomeDisabled) {
                this.onBlur({});
            }
        }
        getTriggers() {
            return { blur: this.markAsBlurred, focus: this.markAsFocused };
        }
        render() {
            const reference = isStatelessComponent(Component)
                ? undefined
                : (ref) => {
                    this.wrappedComponentRef = ref;
                };
            const { className, onBlur, onFocus, ...rest } = this.props;
            return (React.createElement(Component, { ...rest, ref: reference, className: st(classes.root, {
                    focus: this.state.focus,
                    'focus-visible': this.state.focusVisible,
                }, className, classNames({
                    [shellClasses.hasCustomFocus]: options.hasCustomFocus,
                    [shellClasses.ignoreFocus]: options.ignoreFocus,
                })), onBlur: this.onBlur, onFocus: this.onFocus, 'data-focusable-focus': this.state.focus,
                'data-focusable-focus-visible': this.state.focusVisible }));
        }
    }
    // eslint-disable-next-line react/forbid-foreign-prop-types
    assignPropTypesHack(Focusable, Component.propTypes);
    return isStatelessComponent(Component)
        ? Focusable
        : hoistNonReactMethods(Focusable, Component, {
            delegateTo: (c) => c.wrappedComponentRef,
            hoistStatics: true,
        });
}
//# sourceMappingURL=Focusable.js.map